import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Calendar, FileText, HelpCircle, Mail } from 'lucide-react';

const thankYouContent = {
  booking: {
    title: 'Booking Request Received!',
    message: "We've received your pickup request. Our team will review the details and confirm your pickup window via SMS and email, usually within a few hours.",
    icon: Calendar,
    nextSteps: [
      'Check your email/SMS for confirmation',
      'Stack packaging neatly ready for collection',
      "We'll arrive within your selected timeframe",
    ],
  },
  pricing: {
    title: 'Pricing Request Received!',
    message: "Thanks for your interest! We'll review your details and get back to you with a tailored pricing proposal shortly.",
    icon: FileText,
    nextSteps: [
      'Our team will review your requirements',
      "You'll receive a pricing proposal via email",
      'Feel free to book a pickup in the meantime',
    ],
  },
  subscription: {
    title: 'Subscription Enquiry Received!',
    message: "We've received your subscription enquiry. Our team will put together a package tailored to your volume and contact you soon.",
    icon: Calendar,
    nextSteps: [
      'We\'ll review your job volume and areas',
      'Receive a custom subscription proposal',
      'Start saving on regular pickups',
    ],
  },
  contact: {
    title: 'Message Sent!',
    message: "Thanks for reaching out. We've received your message and will get back to you as soon as possible.",
    icon: Mail,
    nextSteps: [
      'Our team will review your message',
      'Expect a response within 1 business day',
      'Check our FAQs for quick answers',
    ],
  },
};

const ThankYouPage = () => {
  const { type } = useParams();
  const content = thankYouContent[type] || thankYouContent.contact;
  const IconComponent = content.icon;

  return (
    <main className="bg-[#F8FAFC] min-h-[calc(100vh-200px)]">
      <section className="py-10 md:py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-6 animate-fade-in">
            <CheckCircle className="w-10 h-10 text-[#0D9488]" />
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111] animate-fade-in-up">
            {content.title}
          </h1>

          {/* Message */}
          <p className="mt-6 text-lg text-[#475569] leading-relaxed animate-fade-in-up delay-100">
            {content.message}
          </p>

          {/* Next Steps */}
          <div className="mt-10 bg-white rounded-2xl p-8 border border-[#E2E8F0] text-left animate-fade-in-up delay-200">
            <h3 className="font-semibold text-[#111111] mb-4 flex items-center gap-2">
              <IconComponent className="w-5 h-5 text-[#0D9488]" />
              What happens next?
            </h3>
            <ul className="space-y-3">
              {content.nextSteps.map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#F0FDFA] flex items-center justify-center text-xs font-semibold text-[#0D9488]">
                    {index + 1}
                  </span>
                  <span className="text-[#475569]">{step}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4 animate-fade-in-up delay-300">
            <Link to="/">
              <Button className="bg-[#0D9488] hover:bg-[#0F766E] text-white px-8">
                Back to Home
              </Button>
            </Link>
            {type !== 'booking' && (
              <Link to="/book/waste">
                <Button variant="outline" className="border-[#E2E8F0] text-[#111111] hover:border-[#0D9488] px-8">
                  Book Collection
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
            <Link to="/faqs">
              <Button variant="outline" className="border-[#E2E8F0] text-[#111111] hover:border-[#0D9488] px-8">
                <HelpCircle className="w-4 h-4 mr-2" />
                FAQs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ThankYouPage;
